/**
 * Shared by the web copies of the Documents screens (web app Phase 5). Web
 * only. Categories, types and tags are the phone's, so a document added on
 * either surface files under the same names.
 */
import { shouldRouteToUnlock } from '../../../navigation/postAuthUnlockRoute';
import {
  isRecoveryCodeRequiredError,
  isVaultLockedError,
  promptToSetRecoveryCode,
  promptToUnlockVault,
} from '../../../services/auth/vaultUnlockPrompt';
import type { UnlockReturnTo } from '../../../services/auth/vaultUnlockPrompt';

/**
 * The browser's globals. The app's TypeScript config has no DOM library
 * (the same sources build the phone app), so web-only code reaches them
 * through this one untyped door.
 */
export const browser = globalThis as unknown as { document: any; window: any };

/** What a window drag event carries, as far as these screens read it. */
export interface FileDragEvent {
  dataTransfer?: { types: ArrayLike<string>; files: ArrayLike<File> } | null;
  preventDefault(): void;
}

export const CATEGORIES = [
  { id: 'personal', name: 'Personal' },
  { id: 'legal', name: 'Legal' },
  { id: 'medical', name: 'Medical' },
  { id: 'financial', name: 'Financial' },
  { id: 'property', name: 'Property' },
  { id: 'insurance', name: 'Insurance' },
  { id: 'digital', name: 'Digital' },
  { id: 'other', name: 'Other' },
];

export const DOCUMENT_TYPES = ['PDF', 'Image', 'Word', 'Excel', 'Other'];

export const TAGS = ['Uploaded', 'Physical', 'Shared', 'Important'];

/** The phone stores a category by id or by name ('legal' or 'Legal'). */
export function categoryId(category: string | undefined): string {
  const c = (category || '').toLowerCase();
  return CATEGORIES.some((x) => x.id === c) ? c : 'other';
}

export function categoryName(category: string | undefined): string {
  return CATEGORIES.find((x) => x.id === categoryId(category))?.name ?? 'Other';
}

export function formatBytes(bytes: number | null | undefined): string {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export function formatDate(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** Open the browser's file dialog; resolves with the chosen files (none if cancelled). */
export function chooseFiles(multiple: boolean): Promise<File[]> {
  return new Promise((resolve) => {
    const input = browser.document.createElement('input');
    input.type = 'file';
    input.multiple = multiple;
    input.style.display = 'none';
    input.addEventListener('change', () => {
      resolve(Array.from(input.files ?? []));
      input.remove();
    });
    input.addEventListener('cancel', () => {
      resolve([]);
      input.remove();
    });
    browser.document.body.appendChild(input);
    input.click();
  });
}

/** Save a Blob under a file name through the browser's download. */
export function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = browser.document.createElement('a');
  a.href = url;
  a.download = fileName;
  browser.document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

const EXTENSION_FOR_MIME: Record<string, string> = {
  'application/pdf': 'pdf',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/heic': 'heic',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'text/plain': 'txt',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
};

/** A download name: the document's name plus the extension its type implies. */
export function downloadName(name: string, mimeType: string | null | undefined): string {
  const base = (name || 'Document').replace(/[\\/:*?"<>|]/g, ' ').trim() || 'Document';
  const ext = EXTENSION_FOR_MIME[(mimeType || '').toLowerCase()];
  return ext && !base.toLowerCase().endsWith(`.${ext}`) ? `${base}.${ext}` : base;
}

/**
 * Run something that needs the vault key. A browser holds the key only
 * after the recovery code is entered this visit, so ask first when it is
 * missing, and again if the key turns out to be locked.
 * Returns false when the user was sent to unlock.
 */
export async function withVaultKey(
  returnTo: UnlockReturnTo,
  run: () => Promise<void>,
): Promise<boolean> {
  if (await shouldRouteToUnlock()) {
    promptToUnlockVault('your documents are', returnTo);
    return false;
  }
  try {
    await run();
    return true;
  } catch (error) {
    if (isVaultLockedError(error)) {
      promptToUnlockVault('your documents are', returnTo);
      return false;
    }
    if (isRecoveryCodeRequiredError(error)) {
      promptToSetRecoveryCode(returnTo);
      return false;
    }
    throw error;
  }
}

/** A readable message from an API or crypto error. */
export function errorMessage(error: unknown, fallback: string): string {
  const e = error as { response?: { data?: { message?: string | string[] } }; message?: string };
  const api = e?.response?.data?.message;
  if (Array.isArray(api)) return api.join(' ');
  if (typeof api === 'string' && api) return api;
  if (e?.message && !/status code/i.test(e.message)) return e.message;
  return fallback;
}
